
import { useState, useCallback, useMemo, useEffect } from 'react';
import type { StateNodeData, AslState } from '@/types/workflow-graph';
import type { 
    TaskConfig, 
    TabId, 
    RetryRule, 
    CatchRule, 
    ParameterValue,
    CapabilityMetadata,
    RetryPresetKey,
} from '../types';
import { RETRY_PRESETS, getDefaultTaskConfig } from '../types';

interface UseTaskConfigOptions {
    nodeData: StateNodeData | null;
    onSave?: (config: TaskConfig, aslState: Partial<AslState>) => void;
    availableNodes?: string[];          // For catch rule target selection
    capabilities?: CapabilityMetadata[]; // Available capabilities
}

interface UseTaskConfigReturn {
    // Form state
    config: TaskConfig;
    isDirty: boolean;
    validationErrors: Record<string, string>;
    
    // Tab state
    activeTab: TabId;
    setActiveTab: (tab: TabId) => void;
    
    // Selected capability metadata
    selectedCapability: CapabilityMetadata | null;
    
    // Field updaters
    updateCapability: (key: string) => void;
    updateInputPath: (path: string) => void;
    updateParameter: (field: string, value: ParameterValue) => void;
    removeParameter: (field: string) => void;
    updateResultPlacement: (placement: TaskConfig['resultPlacement']) => void;
    updateResultPath: (path: string) => void;
    updateOutputPath: (path: string) => void;
    updateTimeout: (seconds: number | undefined) => void;
    updateTimeoutPath: (path: string | undefined) => void;
    updateHeartbeat: (seconds: number | undefined) => void;
    addRetryRule: () => void;
    updateRetryRule: (id: string, updates: Partial<RetryRule>) => void;
    removeRetryRule: (id: string) => void;
    applyRetryPreset: (preset: RetryPresetKey) => void;
    addCatchRule: () => void;
    updateCatchRule: (id: string, updates: Partial<CatchRule>) => void;
    removeCatchRule: (id: string) => void;
    updateComment: (comment: string) => void;
    updateCustomAttribute: (key: string, value: string) => void;
    removeCustomAttribute: (key: string) => void;
    
    // Actions
    save: () => void;
    reset: () => void;
    validate: () => boolean;
    
    // Export
    toAslState: () => Partial<AslState>;
}

/**
 * Parse AslState into TaskConfig for editing.
 */
function parseAslState(state: AslState): TaskConfig {
    // Extract capability key from Resource
    let capabilityKey = '';
    if (state.Resource) {
        // Handle formats: "tool:name", "agent:name", "model:name", "workflow:name"
        capabilityKey = state.Resource.replace(/^(agent|tool|model|workflow):/, '');
    }
    
    // Parse parameters - detect source type
    const parameters: Record<string, ParameterValue> = {};
    if (state.Parameters) {
        for (const [key, value] of Object.entries(state.Parameters)) {
            if (key.endsWith('.$')) {
                // This is a JSONPath reference
                const fieldName = key.slice(0, -2);
                const strValue = String(value);
                if (strValue.startsWith('$$.')) {
                    // Runtime variable reference
                    parameters[fieldName] = {
                        source: 'runtime',
                        value: strValue.slice(3), // Remove '$$.'
                    };
                } else {
                    // State reference
                    parameters[fieldName] = {
                        source: 'state',
                        value: strValue,
                    };
                }
            } else {
                // Constant value
                parameters[key] = {
                    source: 'constant',
                    value: typeof value === 'string' ? value : JSON.stringify(value),
                };
            }
        }
    }
    
    // Determine result placement
    let resultPlacement: TaskConfig['resultPlacement'] = 'merge';
    let resultPath = '$.result';
    if (state.ResultPath === null) {
        resultPlacement = 'discard';
    } else if (state.ResultPath === undefined || state.ResultPath === '$') {
        resultPlacement = 'replace';
    } else {
        resultPlacement = 'merge';
        resultPath = state.ResultPath;
    }
    
    return {
        capabilityKey,
        capabilityVersion: undefined, // TODO: Parse from resource if versioned
        inputPath: state.InputPath || '$',
        parameters,
        resultPlacement,
        resultPath,
        outputPath: state.OutputPath,
        resultSelector: state.ResultSelector as Record<string, string> | undefined,
        timeoutSeconds: state.TimeoutSeconds,
        timeoutSecondsPath: state.TimeoutSecondsPath,
        heartbeatSeconds: state.HeartbeatSeconds,
        retry: (state.Retry || []).map((r, i) => ({
            id: `retry-${i}`,
            errorTypes: r.ErrorEquals,
            maxAttempts: r.MaxAttempts || 3,
            initialDelay: r.IntervalSeconds || 1,
            backoffRate: r.BackoffRate || 2,
            maxDelay: r.MaxDelaySeconds,
            jitter: r.JitterStrategy === 'FULL',
        })),
        catch: (state.Catch || []).map((c, i) => ({
            id: `catch-${i}`,
            errorTypes: c.ErrorEquals,
            targetNode: c.Next,
            resultPath: c.ResultPath ?? undefined,
        })),
        comment: state.Comment,
        customAttributes: {},
    };
}

/**
 * Convert TaskConfig back to AslState for saving.
 */
function toAslState(config: TaskConfig, resourceType: string): Partial<AslState> {
    const state: Partial<AslState> = {
        Type: 'Task',
        Resource: `${resourceType}:${config.capabilityKey}`,
        Comment: config.comment || undefined,
    };
    
    // Input
    if (config.inputPath && config.inputPath !== '$') {
        state.InputPath = config.inputPath;
    }
    
    if (Object.keys(config.parameters).length > 0) {
        state.Parameters = {};
        for (const [fieldName, param] of Object.entries(config.parameters)) {
            if (param.source === 'state') {
                state.Parameters[`${fieldName}.$`] = param.value;
            } else if (param.source === 'runtime') {
                state.Parameters[`${fieldName}.$`] = `$$.${param.value}`;
            } else {
                // Try to parse JSON for non-string values
                try {
                    const parsed = JSON.parse(param.value);
                    state.Parameters[fieldName] = parsed;
                } catch {
                    state.Parameters[fieldName] = param.value;
                }
            }
        }
    }
    
    // Output
    if (config.resultPlacement === 'discard') {
        state.ResultPath = null;
    } else if (config.resultPlacement === 'merge' && config.resultPath) {
        state.ResultPath = config.resultPath;
    }
    // 'replace' means no ResultPath (default behavior)
    
    if (config.outputPath) {
        state.OutputPath = config.outputPath;
    }
    
    if (config.resultSelector && Object.keys(config.resultSelector).length > 0) {
        state.ResultSelector = config.resultSelector;
    }
    
    // Resilience
    if (config.timeoutSeconds) {
        state.TimeoutSeconds = config.timeoutSeconds;
    }
    if (config.timeoutSecondsPath) {
        state.TimeoutSecondsPath = config.timeoutSecondsPath;
    }
    if (config.heartbeatSeconds) {
        state.HeartbeatSeconds = config.heartbeatSeconds;
    }
    
    if (config.retry.length > 0) {
        state.Retry = config.retry.map((r) => ({
            ErrorEquals: r.errorTypes,
            MaxAttempts: r.maxAttempts,
            IntervalSeconds: r.initialDelay,
            BackoffRate: r.backoffRate,
            ...(r.maxDelay && { MaxDelaySeconds: r.maxDelay }),
            ...(r.jitter && { JitterStrategy: 'FULL' as const }),
        }));
    }
    
    // Errors
    if (config.catch.length > 0) {
        state.Catch = config.catch.map((c) => ({
            ErrorEquals: c.errorTypes,
            Next: c.targetNode,
            ...(c.resultPath && { ResultPath: c.resultPath }),
        }));
    }
    
    return state;
}

export function useTaskConfig({
    nodeData,
    onSave,
    availableNodes = [],
    capabilities = [],
}: UseTaskConfigOptions): UseTaskConfigReturn {
    // Initialize state from node data
    const initialConfig = useMemo(() => {
        if (!nodeData?.state) {
            return getDefaultTaskConfig();
        }
        return parseAslState(nodeData.state);
    }, [nodeData]);
    
    const [config, setConfig] = useState<TaskConfig>(initialConfig);
    const [activeTab, setActiveTab] = useState<TabId>('capability');
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    
    // Reset when node changes
    useEffect(() => {
        setConfig(initialConfig);
        setValidationErrors({});
        setActiveTab('capability');
    }, [initialConfig]);
    
    // Track dirty state
    const isDirty = useMemo(() => {
        return JSON.stringify(config) !== JSON.stringify(initialConfig);
    }, [config, initialConfig]);
    
    // Find selected capability metadata
    const selectedCapability = useMemo(() => {
        return capabilities.find((c) => c.key === config.capabilityKey) || null;
    }, [config.capabilityKey, capabilities]);
    
    // Field updaters
    const updateCapability = useCallback((key: string) => {
        setConfig((prev) => ({
            ...prev,
            capabilityKey: key,
            // Clear parameters when capability changes
            parameters: {},
        }));
    }, []);
    
    const updateInputPath = useCallback((path: string) => {
        setConfig((prev) => ({ ...prev, inputPath: path }));
    }, []);
    
    const updateParameter = useCallback((field: string, value: ParameterValue) => {
        setConfig((prev) => ({
            ...prev,
            parameters: { ...prev.parameters, [field]: value },
        }));
    }, []);
    
    const removeParameter = useCallback((field: string) => {
        setConfig((prev) => {
            const { [field]: _, ...rest } = prev.parameters;
            return { ...prev, parameters: rest };
        });
    }, []);
    
    const updateResultPlacement = useCallback((placement: TaskConfig['resultPlacement']) => {
        setConfig((prev) => ({ ...prev, resultPlacement: placement }));
    }, []);
    
    const updateResultPath = useCallback((path: string) => {
        setConfig((prev) => ({ ...prev, resultPath: path }));
    }, []);
    
    const updateOutputPath = useCallback((path: string) => {
        setConfig((prev) => ({ ...prev, outputPath: path || undefined }));
    }, []);
    
    const updateTimeout = useCallback((seconds: number | undefined) => {
        setConfig((prev) => ({ ...prev, timeoutSeconds: seconds }));
    }, []);
    
    const updateTimeoutPath = useCallback((path: string | undefined) => {
        setConfig((prev) => ({ ...prev, timeoutSecondsPath: path }));
    }, []);
    
    const updateHeartbeat = useCallback((seconds: number | undefined) => {
        setConfig((prev) => ({ ...prev, heartbeatSeconds: seconds }));
    }, []);
    
    const addRetryRule = useCallback(() => {
        setConfig((prev) => ({
            ...prev,
            retry: [
                ...prev.retry,
                {
                    id: `retry-${Date.now()}`,
                    errorTypes: ['States.ALL'],
                    maxAttempts: 3,
                    initialDelay: 1,
                    backoffRate: 2,
                    jitter: false,
                },
            ],
        }));
    }, []);
    
    const updateRetryRule = useCallback((id: string, updates: Partial<RetryRule>) => {
        setConfig((prev) => ({
            ...prev,
            retry: prev.retry.map((r) => (r.id === id ? { ...r, ...updates } : r)),
        }));
    }, []);
    
    const removeRetryRule = useCallback((id: string) => {
        setConfig((prev) => ({
            ...prev,
            retry: prev.retry.filter((r) => r.id !== id),
        }));
    }, []);
    
    const applyRetryPreset = useCallback((preset: RetryPresetKey) => {
        const presetConfig = RETRY_PRESETS[preset];
        if (presetConfig) {
            setConfig((prev) => ({
                ...prev,
                retry: presetConfig.config.map((r) => ({
                    ...r,
                    id: `retry-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    // Convert readonly array to mutable array
                    errorTypes: [...r.errorTypes],
                })),
            }));
        }
    }, []);
    
    const addCatchRule = useCallback(() => {
        setConfig((prev) => ({
            ...prev,
            catch: [
                ...prev.catch,
                {
                    id: `catch-${Date.now()}`,
                    errorTypes: ['States.ALL'],
                    targetNode: availableNodes[0] || '',
                },
            ],
        }));
    }, [availableNodes]);
    
    const updateCatchRule = useCallback((id: string, updates: Partial<CatchRule>) => {
        setConfig((prev) => ({
            ...prev,
            catch: prev.catch.map((c) => (c.id === id ? { ...c, ...updates } : c)),
        }));
    }, []);
    
    const removeCatchRule = useCallback((id: string) => {
        setConfig((prev) => ({
            ...prev,
            catch: prev.catch.filter((c) => c.id !== id),
        }));
    }, []);
    
    const updateComment = useCallback((comment: string) => {
        setConfig((prev) => ({ ...prev, comment: comment || undefined }));
    }, []);
    
    const updateCustomAttribute = useCallback((key: string, value: string) => {
        setConfig((prev) => ({
            ...prev,
            customAttributes: { ...prev.customAttributes, [key]: value },
        }));
    }, []);
    
    const removeCustomAttribute = useCallback((key: string) => {
        setConfig((prev) => {
            const { [key]: _, ...rest } = prev.customAttributes || {};
            return { ...prev, customAttributes: rest };
        });
    }, []);
    
    // Validation
    const validate = useCallback(() => {
        const errors: Record<string, string> = {};
        
        if (!config.capabilityKey) {
            errors.capability = 'Capability is required';
        }
        
        // Validate required parameters based on schema
        if (selectedCapability?.inputSchema?.required) {
            for (const field of selectedCapability.inputSchema.required) {
                const param = config.parameters[field];
                if (!param || !param.value) {
                    errors[`param.${field}`] = `${field} is required`;
                }
            }
        }
        
        // Validate catch rules have targets
        config.catch.forEach((c, i) => {
            if (!c.targetNode) {
                errors[`catch.${i}`] = 'Target node is required';
            }
        });
        
        // Validate result path format for merge
        if (config.resultPlacement === 'merge' && config.resultPath) {
            if (!config.resultPath.startsWith('$.')) {
                errors.resultPath = 'Result path must start with $. (e.g., $.result)';
            }
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [config, selectedCapability]);
    
    // Actions
    const save = useCallback(() => {
        if (!validate()) return;
        
        const resourceType = selectedCapability?.type || 'tool';
        const aslState = toAslState(config, resourceType);
        onSave?.(config, aslState);
    }, [config, selectedCapability, validate, onSave]);
    
    const reset = useCallback(() => {
        setConfig(initialConfig);
        setValidationErrors({});
    }, [initialConfig]);
    
    const exportToAsl = useCallback(() => {
        const resourceType = selectedCapability?.type || 'tool';
        return toAslState(config, resourceType);
    }, [config, selectedCapability]);
    
    return {
        config,
        isDirty,
        validationErrors,
        activeTab,
        setActiveTab,
        selectedCapability,
        updateCapability,
        updateInputPath,
        updateParameter,
        removeParameter,
        updateResultPlacement,
        updateResultPath,
        updateOutputPath,
        updateTimeout,
        updateTimeoutPath,
        updateHeartbeat,
        addRetryRule,
        updateRetryRule,
        removeRetryRule,
        applyRetryPreset,
        addCatchRule,
        updateCatchRule,
        removeCatchRule,
        updateComment,
        updateCustomAttribute,
        removeCustomAttribute,
        save,
        reset,
        validate,
        toAslState: exportToAsl,
    };
}

